import { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../utils/config/config";

export const useMessages = () => {
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0,
  });

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axiosInstance.get("messages/contacts");
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching contacts:", err);
    }
  }, []);

  const fetchMessages = useCallback(async (page = 1) => {
    try {
      const response = await axiosInstance.get(
        `messages/history?page=${page}&limit=50`,
      );
      if (response.data.success) {
        setMessages(response.data.data.messages);
        setPagination(response.data.data.pagination);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  }, []);

  const sendMessage = useCallback(
    async ({ toUserId, subject, body }) => {
      setSending(true);
      setError(null);
      try {
        const response = await axiosInstance.post("messages/send", {
          toUserId,
          subject,
          body,
        });
        if (response.data.success) {
          setSuccess(true);
          // Refresh messages and contacts after sending
          await Promise.all([fetchMessages(), fetchContacts()]);
          return response.data;
        } else {
          throw new Error(response.data.message || "Failed to send message.");
        }
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Failed to send message.";
        setError(message);
        throw new Error(message);
      } finally {
        setSending(false);
      }
    },
    [fetchMessages, fetchContacts],
  );

  const markAsRead = useCallback(
    async (messageId) => {
      try {
        await axiosInstance.put(`messages/read/${messageId}`);
        // Update locally
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, readAt: new Date() } : msg,
          ),
        );
      } catch (err) {
        console.error("Error marking message as read:", err);
      }
    },
    [],
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchContacts(), fetchMessages()]);
      setLoading(false);
    };
    loadData();
  }, [fetchContacts, fetchMessages]);

  return {
    contacts,
    messages,
    loading,
    sending,
    error,
    success,
    pagination,
    setError,
    setSuccess,
    sendMessage,
    markAsRead,
    fetchMessages,
    fetchContacts,
  };
};
